import { QuestionIcon } from '@phosphor-icons/react'
import { utilities } from '@pixee/design-system'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { match } from 'ts-pattern'
import { ScaleBar } from '../../components/scale-bar.tsx'
import { columnWidth } from '../../components/tables/repositories-table.columns.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { type RepositoryRiskScore, useRepositoryRiskScore } from '../../hooks/use-repository-risk-scores.ts'
import { type Repository } from '../../utils/api-client/api-schemas.ts'
import { formatRelativeTime } from '../../utils/date-utils.ts'
import { formatPercentage } from '../../utils/text-utils.ts'
import { SCORECARDS_PHASE_LABELS } from '../../utils/threat-model/scorecards-phase-labels.ts'
import { RISK_RAMP } from './repo-risk-donut.css.ts'
import { RepoRiskDonut } from './repo-risk-donut.tsx'
import * as styles from './repository-risk.columns.css.ts'

/**
 * The risk column for the repositories table.
 *
 * Built here rather than beside the table's own columns because it needs the risk types, which live
 * with this route — `architecture.test.ts` forbids a component importing from `routes/`. The page hands
 * it to `RepositoriesTable`, which splices it in before Actions.
 */

const columnHelper = createColumnHelper<Repository>()

type RiskCellPureProps = {
  repositoryId: string
  score: RepositoryRiskScore
  onSelectRepository: (repositoryId: string) => unknown
}

/**
 * Finds its own row rather than being handed one.
 *
 * The column factory used to close over a `Map` of query data supplied by the page, which meant the page
 * was shipping fetched rows into `components/tables/`. The cell is a component, so it can do its own
 * lookup — react-query serves the same key from cache, so this costs no extra request. The comparator and
 * the filter still take the whole set, because ordering a page genuinely needs all of it.
 */
const RiskCell: React.FC<{ repositoryId: string; onSelectRepository: (repositoryId: string) => unknown }> = ({
  repositoryId,
  onSelectRepository,
}) => (
  <RiskCellPure
    repositoryId={repositoryId}
    score={useRepositoryRiskScore(repositoryId)}
    onSelectRepository={onSelectRepository}
  />
)

/**
 * The score, the summary it explains, the confirmed-finding count and when the model was generated — one
 * column.
 *
 * These were four columns and read as unrelated facts. The score is the headline, the summary says why it
 * is what it is, the count is the evidence and the timestamp says how stale that evidence is. Keeping them
 * in one cell also means no column gap can open up between the donut and the sentence it belongs to.
 */
const RiskCellPure: React.FC<RiskCellPureProps> = ({ repositoryId, score, onSelectRepository }) => {
  if (score.state === 'loading') {
    return (
      <div className={styles.riskCell} aria-busy>
        <span className={styles.skeletonDonut} />
        <div className={styles.riskBody}>
          <span className={styles.skeletonLine.summary} />
          <span className={styles.skeletonLine.meta} />
        </div>
        <span className={utilities.visuallyHidden} role="status">
          Loading risk score
        </span>
      </div>
    )
  }

  return (
    <div className={styles.riskCell}>
      {score.riskScore === undefined ? (
        <span
          className={styles.scorePending}
          aria-label={score.state === 'absent' ? 'No threat model' : 'Not yet scored'}
        >
          —
        </span>
      ) : (
        // Not a control: the "Details" link below is the way into the drawer, and making the donut a
        // second button would put two tab stops on one destination in every row.
        <RepoRiskDonut riskScore={score.riskScore} />
      )}

      <div className={styles.riskBody}>
        <span className={styles.rationale}>
          {match(score)
            .with({ state: 'absent' }, () => 'No threat model')
            .with({ state: 'processing' }, () => 'Scorecard still generating')
            .with({ state: 'failed' }, () => 'Scorecard generation failed')
            // The weakest dimension, not a description of the mechanism: "derived from the scorecard"
            // was true of every row and so told you nothing about this one. Naming what holds the
            // score down points somewhere.
            .with({ state: 'completed' }, ({ weakest }) =>
              weakest
                ? `Weakest: ${weakest.displayName} at ${formatPercentage(weakest.score)}`
                : 'Scorecard scored no dimensions'
            )
            // `loading` is listed although the early return above means it never renders: `RepositoryRiskScore`
            // is one object type with a union-typed `state`, not a discriminated union, so that return
            // narrows `score.state` at the check and not the object afterwards. Naming it is what lets this
            // be `exhaustive` rather than `otherwise` — so a new state fails the build instead of quietly
            // reading as "No threat model".
            .with({ state: 'loading' }, () => undefined)
            .exhaustive()}
        </span>

        {score.state !== 'absent' && (
          <span className={styles.metaLine}>
            <span className={styles.meta}>
              {match(score)
                // The phase, not a generic "Generating…": the same words the repository's own Scorecard
                // tab uses, so a reader moving between them sees one vocabulary.
                .with({ state: 'processing' }, ({ phase }) =>
                  phase ? `${SCORECARDS_PHASE_LABELS[phase]}…` : 'Generating…'
                )
                // The reason itself, not a signpost to it: every reported one is a single short
                // sentence, so it fits here and a click to read 26 characters is a click too many.
                .with({ state: 'failed' }, ({ failureReason }) => failureReason ?? 'No reason was reported')
                .with({ state: 'completed' }, ({ generatedAt }) =>
                  generatedAt ? `generated ${formatRelativeTime(generatedAt)}` : undefined
                )
                .with({ state: 'absent' }, () => undefined)
                .with({ state: 'loading' }, () => undefined)
                .exhaustive()}
            </span>
            {/* Only once there is a finished scorecard to explain — mid-generation the drawer would open
                on an assessment that does not exist yet. After a failure it carries the reason. */}
            {/* Only a finished scorecard has anything to explain: mid-generation the drawer would open
                on an assessment that does not exist, and a failure now states its reason in the row. */}
            {score.state === 'completed' && (
              <button
                type="button"
                className={styles.detailsLink}
                // The row's own click-to-navigate stands down for a nested button —
                // `isInteractiveElementClick` already treats one that way.
                onClick={() => onSelectRepository(repositoryId)}
              >
                Details
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  )
}

export const createRiskColumnDefinitions = ({
  onSelectRepository,
  onOpenAboutRisk,
}: {
  onSelectRepository: (repositoryId: string) => unknown
  onOpenAboutRisk: () => unknown
}): ColumnDef<Repository>[] =>
  [
    columnHelper.display({
      id: 'risk',
      // The header sits at the column's left edge, which is where the donut is, so "RISK" labels the score
      // and the summary it explains at once.
      meta: { width: columnWidth('risk-and-beacon', 'risk') },
      header: () => (
        <span className={styles.riskHeader}>
          RISK
          {/* On the header rather than in a row: the dimensions describe the column, not one repository. */}
          <TooltipWithHover
            side="bottom"
            maxWidth={320}
            trigger={
              <button
                type="button"
                className={styles.aboutRiskButton}
                aria-label="About risk"
                onClick={onOpenAboutRisk}
              >
                <QuestionIcon size={14} weight="bold" aria-hidden />
              </button>
            }
          >
            {/* The scale on hover, the detail on click. A reader who only needs to know which way the
                number runs gets it without opening anything; the ten dimensions and the posture caveat are
                worth a click and live in the drawer. */}
            <span className={styles.aboutRiskTip}>
              <ScaleBar stops={RISK_RAMP} labels={['0 · Safest', '100 · Riskiest']} />
              {/* A real control, and clickable: Radix portals tooltip content out of the trigger, so this is
                  not a nested button, and hoverable content means the pointer reaches it. `tabIndex={-1}`
                  keeps it out of the tab order because the trigger is the keyboard path to the same action —
                  two stops for one drawer would be noise. */}
              <button type="button" tabIndex={-1} className={styles.aboutRiskTipAction} onClick={onOpenAboutRisk}>
                About risk
              </button>
            </span>
          </TooltipWithHover>
        </span>
      ),
      cell: ({ row }) => <RiskCell repositoryId={row.original.id} onSelectRepository={onSelectRepository} />,
    }),
  ] as ColumnDef<Repository>[]
